import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { setBreadcrumbLabel } from '../../shell/breadcrumbLabels.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface VendorDetail {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly legalName: string;
  readonly contactPerson: string;
  readonly mobileNumber: string;
  readonly email: string;
  readonly address: string | null;
  readonly gstNumber: string;
  readonly panNumber: string;
  readonly tanNumber: string | null;
  readonly bankAccountNumber: string | null;
  readonly ifscCode: string | null;
  readonly performanceRating: number | null;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
  readonly gstCertDocRef: string | null;
  readonly panCardDocRef: string | null;
  readonly cancelledChequeDocRef: string | null;
  readonly territoryCoverage: readonly string[] | null;
  readonly workSpecializations: readonly string[] | null;
}

export function VendorDetailPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [v, setV] = useState<VendorDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<VendorDetail>(`/v1/master/vendors/${id}`, { token })
      .then((r) => { if (alive) setV(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, id]);

  useEffect(() => {
    if (!id || !v) return undefined;
    setBreadcrumbLabel(id, `${v.vendorCode} — ${v.vendorName}`);
    return () => setBreadcrumbLabel(id, null);
  }, [id, v]);

  if (error) return <main aria-label="vendor-detail"><p role="alert">{error}</p></main>;
  if (!v) return <main aria-label="vendor-detail"><p>{t('vendors.detail.loading')}</p></main>;

  // The entity stores one column per KYC document, not a collection.
  const kyc = [
    { kind: 'GST_CERTIFICATE', ref: v.gstCertDocRef },
    { kind: 'PAN_CARD', ref: v.panCardDocRef },
    { kind: 'CANCELLED_CHEQUE', ref: v.cancelledChequeDocRef },
  ].filter((d): d is { kind: string; ref: string } => !!d.ref);

  return (
    <main aria-label="vendor-detail">
      <h1>{v.vendorName}</h1>
      <p aria-label="vendor-code">{t('vendors.detail.code')}: {v.vendorCode}</p>
      <p aria-label="vendor-legal">{t('vendors.detail.legal')}: {v.legalName}</p>
      <p aria-label="vendor-contact">{v.contactPerson} · {v.mobileNumber} · {v.email}</p>
      <p aria-label="vendor-gstin">{t('vendors.detail.gstin')}: {v.gstNumber}</p>
      <p aria-label="vendor-pan">{t('vendors.detail.pan')}: {v.panNumber}</p>
      <p aria-label="vendor-tan">{t('vendors.create.tan')}: {v.tanNumber ?? '—'}</p>
      <p aria-label="vendor-address">{t('vendors.create.address')}: {v.address ?? '—'}</p>
      <p aria-label="vendor-bank">
        {t('vendors.create.secBank')}: {v.bankAccountNumber ?? '—'} / {v.ifscCode ?? '—'}
      </p>
      <p aria-label="vendor-rating">{t('vendors.detail.rating')}: {v.performanceRating ?? '—'}</p>
      <p aria-label="vendor-validity">{t('vendors.detail.valid')}: {day(v.effectiveFromDate)} → {day(v.effectiveToDate)}</p>

      <h2>{t('vendors.detail.kyc', { count: kyc.length })}</h2>
      <ul aria-label="kyc-list">
        {kyc.map((d) => (
          <li key={d.kind} aria-label={`kyc-${d.kind}`}>{d.kind}: {d.ref}</li>
        ))}
      </ul>

      <h2>{t('vendors.detail.coverage')}</h2>
      <p aria-label="vendor-territories">{t('vendors.detail.territories')}: {(v.territoryCoverage ?? []).join(', ') || '—'}</p>
      <p aria-label="vendor-specs">{t('vendors.detail.specs')}: {(v.workSpecializations ?? []).join(', ') || '—'}</p>
    </main>
  );
}
